import { useMemo } from 'react';
import { useAuth as useAuthContext } from '../context/AuthContext';

interface UseAuthResult {
  user: ReturnType<typeof useAuthContext>['state']['user'];
  token: ReturnType<typeof useAuthContext>['state']['token'];
  isAuthenticated: ReturnType<typeof useAuthContext>['state']['isAuthenticated'];
  isLoading: ReturnType<typeof useAuthContext>['state']['isLoading'];
  error: ReturnType<typeof useAuthContext>['state']['error'];
  login: ReturnType<typeof useAuthContext>['login'];
  logout: ReturnType<typeof useAuthContext>['logout'];
  updateUser: ReturnType<typeof useAuthContext>['updateUser'];
  clearError: ReturnType<typeof useAuthContext>['clearError'];
}

/**
 * Convenience hook that exposes the auth context state in a flattened shape
 * along with the available auth actions. This maintains backwards
 * compatibility with the previous `useAuth` hook that lived in `src/hooks`.
 */
export function useAuth(): UseAuthResult {
  const { state, login, logout, updateUser, clearError } = useAuthContext();

  return useMemo(
    () => ({
      user: state.user,
      token: state.token,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      error: state.error,
      login,
      logout,
      updateUser,
      clearError,
    }),
    [state.user, state.token, state.isAuthenticated, state.isLoading, state.error, login, logout, updateUser, clearError],
  );
}

/**
 * Helper hook to access only the auth state without actions.
 */
export function useAuthState() {
  const { state } = useAuthContext();
  return state;
}
